/**
 * 电商详情图节点：分镜位模板与 Seedream 提示词
 */

import { getEcomPromptExpertBuiltinSkill } from '@/utils/ecomPromptExpertSkill'
import {
  ECOM_DETAIL_DEFAULT_OUTPUT_LANGUAGE,
  ECOM_DETAIL_OUTPUT_LANGUAGES,
  getEcomDetailCopyLanguageBlock,
  getEcomUpstreamImageAnalysisPrompt,
  isEcomDetailEnglishOutput,
  normalizeEcomDetailOutputLanguage
} from '@/utils/ecomDetailLanguage'
// import { getEcomVisualPromptBuiltinSkill } from '@/utils/ecomVisualPromptSkill'

export {
  ECOM_DETAIL_OUTPUT_LANGUAGES,
  ECOM_DETAIL_DEFAULT_OUTPUT_LANGUAGE,
  normalizeEcomDetailOutputLanguage,
  isEcomDetailEnglishOutput,
  getEcomDetailCopyLanguageBlock,
  getEcomDetailOutputLanguageLabel
} from '@/utils/ecomDetailLanguage'

/** 接口不可用时的内置 Skill（与首页热门 Skills 语义一致） */
export const ECOM_DETAIL_BUILTIN_SKILLS = [
  getEcomPromptExpertBuiltinSkill()
  // 暂时隐藏其余内置 Skill
  // getEcomVisualPromptBuiltinSkill(),
  // {
  //   id: 'ecom-skill-main',
  //   title: '电商主图套装',
  //   desc: '白底主图 + 干净光影，适合淘宝/京东列表',
  //   prompt: '专业电商主图：商品主体突出、背景简洁、商业摄影布光、高清质感',
  //   image: ''
  // },
  // {
  //   id: 'ecom-skill-seed',
  //   title: '种草详情',
  //   desc: '小红书风格场景与柔和色调',
  //   prompt: '种草风电商详情：真实生活场景、柔和自然光、轻奢质感、留白排版区',
  //   image: ''
  // },
  // {
  //   id: 'ecom-skill-promo',
  //   title: '促销爆款',
  //   desc: '高对比卖点与活动氛围',
  //   prompt: '促销向详情图：高对比色彩、醒目卖点区块、节日/大促氛围但避免侵权品牌元素',
  //   image: ''
  // }
]

/** DeepSeek 优化各模板位文生图提示词时的 system */
export const ECOM_DETAIL_DEEPSEEK_SYSTEM = `你是电商详情图提示词专家。根据商品信息、平台风格与版式要求，只输出一段可直接用于文生图模型的中文提示词。
要求：单段连贯描述，包含画面主体、构图、光影、排版留白与氛围；不要解释、不要 markdown、不要标题编号。`

export const ECOM_DETAIL_PLATFORMS = [
  { id: 'taobao', label: '淘宝 / 天猫' },
  { id: 'jd', label: '京东' },
  { id: 'douyin', label: '抖音 / 小店' },
  { id: 'xiaohongshu', label: '小红书' },
  { id: 'generic', label: '通用电商' }
]

export const ECOM_DETAIL_SLOT_DEFS = [
  {
    id: 'hero',
    label: '主图',
    aspectKey: '1x1_2k',
    defaultOn: true,
    hint: '白底/场景主图，突出商品主体'
  },
  {
    id: 'selling1',
    label: '卖点图 1',
    aspectKey: '3x4_2k',
    defaultOn: true,
    hint: '核心卖点 + 短文案排版'
  },
  {
    id: 'selling2',
    label: '卖点图 2',
    aspectKey: '3x4_2k',
    defaultOn: true,
    hint: '功能/材质/场景其一'
  },
  {
    id: 'selling3',
    label: '卖点图 3',
    aspectKey: '3x4_2k',
    defaultOn: false,
    hint: '补充卖点或对比示意'
  },
  {
    id: 'detail',
    label: '详情氛围图',
    aspectKey: '9x16_2k',
    defaultOn: true,
    hint: '竖版详情页氛围/场景延展'
  },
  {
    id: 'spec',
    label: '参数示意',
    aspectKey: '16x9_2k',
    defaultOn: false,
    hint: '规格参数信息图风格（示意性排版）'
  }
]

const PLATFORM_STYLE = {
  taobao: '淘宝天猫详情页风格：信息清晰、促销感适中、主图干净',
  jd: '京东商品详情风格：偏品质感、对比清晰、信任背书',
  douyin: '抖音电商短视频封面/详情风格：高对比、吸睛、适合竖屏',
  xiaohongshu: '小红书种草风格：生活场景、柔和光线、真实质感',
  generic: '主流电商平台商品详情图风格'
}

export function normalizeEcomDetailSlots(slots) {
  const src = slots && typeof slots === 'object' ? slots : {}
  const out = {}
  for (const s of ECOM_DETAIL_SLOT_DEFS) {
    out[s.id] = src[s.id] != null ? !!src[s.id] : !!s.defaultOn
  }
  return out
}

export function getEnabledEcomDetailSlots(slots) {
  const norm = normalizeEcomDetailSlots(slots)
  return ECOM_DETAIL_SLOT_DEFS.filter(s => norm[s.id])
}

export function ecomDetailOutputUrls(node) {
  const arr = node && node.data && Array.isArray(node.data.outputImages) ? node.data.outputImages : []
  const urls = []
  for (const row of arr) {
    const u = row && row.url != null ? String(row.url).trim() : ''
    if (u) urls.push(u)
  }
  return urls
}

function platformStyle(platformId) {
  const id = String(platformId || 'generic').trim()
  return PLATFORM_STYLE[id] || PLATFORM_STYLE.generic
}

function skillBlock(ctx) {
  const skill = ctx && ctx.skill && typeof ctx.skill === 'object' ? ctx.skill : null
  if (!skill) return ''
  const title = String(skill.title || '').trim()
  const prompt = String(skill.prompt || skill.desc || '').trim()
  if (!title && !prompt) return ''
  const lines = ['【Skill 工作流】']
  if (title) lines.push(`名称：${title}`)
  if (prompt) lines.push(`创作要求：${prompt}`)
  return lines.join('\n')
}

/** @deprecated 使用 getEcomUpstreamImageAnalysisPrompt(outputLanguage) */
export const ECOM_UPSTREAM_IMAGE_ANALYSIS_PROMPT = getEcomUpstreamImageAnalysisPrompt('zh')

/**
 * @param {string[]} imageUrls
 * @param {string} [textHints] 用户已填写的名称/卖点等
 * @param {string} [outputLanguage] zh | en
 * @returns {object[]|null} volc_chat messages
 */
export function buildEcomUpstreamImageVisionMessages(imageUrls, textHints, outputLanguage) {
  const urls = (Array.isArray(imageUrls) ? imageUrls : [])
    .map(u => String(u || '').trim())
    .filter(Boolean)
    .slice(0, 4)
  if (!urls.length) return null
  const hint = String(textHints || '').trim()
  const lang = normalizeEcomDetailOutputLanguage(outputLanguage)
  const analysisPrompt = getEcomUpstreamImageAnalysisPrompt(lang)
  const hintLabel = isEcomDetailEnglishOutput(lang) ? 'User notes' : '用户补充文字'
  const intro = hint
    ? `${analysisPrompt}\n\n【${hintLabel}】\n${hint}`
    : analysisPrompt
  const content = [{ type: 'text', text: intro }]
  for (let i = 0; i < urls.length; i++) {
    content.push({ type: 'image_url', image_url: { url: urls[i] } })
  }
  return [{ role: 'user', content }]
}

function productBlock(ctx) {
  const name = String(ctx.productName || '').trim()
  const points = String(ctx.sellingPoints || '').trim()
  const extra = String(ctx.extraPrompt || '').trim()
  const up = String(ctx.upstreamText || '').trim()
  const imgInfo = String(ctx.upstreamImageInfo || '').trim()
  const lines = []
  if (imgInfo) lines.push(`【上游商品图视觉分析】\n${imgInfo}`)
  if (name) lines.push(`商品名称：${name}`)
  if (points) lines.push(`核心卖点：\n${points}`)
  if (extra) lines.push(`补充要求：${extra}`)
  if (up) lines.push(`上游参考文案：\n${up}`)
  const sk = skillBlock(ctx)
  if (sk) lines.push(sk)
  return lines.join('\n')
}

const SLOT_PROMPT_TAIL = {
  hero:
    '生成一张电商主图。商品居中、主体清晰，背景简洁或轻度场景，适合作为列表主图。不要出现真实品牌 Logo 与侵权文字。',
  selling1:
    '生成一张竖版卖点图。突出第一个核心卖点，留出标题区与短文案区（可用占位文案块），排版专业、易读。',
  selling2:
    '生成一张竖版卖点图。展示功能亮点或使用场景，图文分区明确，适合详情页中段。',
  selling3:
    '生成一张竖版卖点图。展示补充卖点、对比或配件示意，风格与前几张统一。',
  detail:
    '生成一张竖版详情氛围图。生活化/使用场景延展，强调质感与氛围，适合长详情页视觉段落。',
  spec:
    '生成一张横版参数示意信息图。表格/图标化参数排版（可用示意数据），风格简洁专业。'
}

/**
 * @param {string} slotId
 * @param {object} ctx
 */
export function buildEcomDetailSlotPrompt(slotId, ctx) {
  const id = String(slotId || '').trim()
  const tail = SLOT_PROMPT_TAIL[id] || '生成一张电商商品详情配图。'
  const base = productBlock(ctx)
  const plat = platformStyle(ctx.platform)
  const lang = normalizeEcomDetailOutputLanguage(ctx && ctx.outputLanguage)
  const copyTail = isEcomDetailEnglishOutput(lang)
    ? 'Clean commercial lighting; readable English marketing copy areas on the image (no fake brand logos).'
    : '画面干净、商业摄影级光影，中文排版区域清晰但不要求可读的具体品牌名。'
  return [
    '你是电商视觉设计师。输出单张高质量商品营销图，无水印、无二维码。',
    `平台风格：${plat}`,
    getEcomDetailCopyLanguageBlock(lang),
    base || '（请根据参考图理解商品品类与外观）',
    tail,
    copyTail
  ]
    .filter(Boolean)
    .join('\n\n')
}

export {
  isEcomPromptExpertSkill,
  ECOM_SKILL_PROMPT_EXPERT_ID
} from '@/utils/ecomPromptExpertSkill'
export {
  isEcomVisualPromptSkill,
  ECOM_SKILL_VISUAL_PROMPT_ID
} from '@/utils/ecomVisualPromptSkill'

export function resolveEcomDetailSkill(stored, skillList) {
  if (stored && typeof stored === 'object' && (stored.title || stored.prompt)) {
    return stored
  }
  const id = stored && stored.id != null ? String(stored.id) : ''
  if (!id) return null
  const list = Array.isArray(skillList) ? skillList : []
  return list.find(s => s && String(s.id) === id) || null
}

export {
  ECOM_SEEDREAM_SIZE_DETAIL,
  ECOM_SEEDREAM_SIZE_MAIN,
  SEEDREAM_MIN_PIXELS,
  ecomPlanSizeToAspectKey,
  normalizeEcomPromptPlanRow,
  resolveEcomSeedreamOutputSize
} from '@/utils/ecomSeedreamSizes'

export function exportEcomDetailNodeText(node) {
  if (!node || node.type !== 'ecomDetail') return ''
  const d = node.data || {}
  const name = String(d.productName || '').trim()
  const pts = String(d.sellingPoints || '').trim()
  const skill =
    d.selectedSkill && d.selectedSkill.title ? `Skill:${d.selectedSkill.title}` : ''
  const imgAnalysis = String(d.upstreamImageAnalysis || '').trim()
  const plans = Array.isArray(d.promptPlans) ? d.promptPlans : []
  const imgs = ecomDetailOutputUrls(node)
  const parts = []
  if (name || pts || skill) {
    parts.push(`【电商详情图】${[name, pts, skill].filter(Boolean).join(' · ')}`)
  }
  if (imgAnalysis) parts.push(`【商品图解析】\n${imgAnalysis}`)
  if (plans.length) {
    parts.push(
      plans
        .map((p, i) => {
          const lab = p && p.label ? String(p.label) : `图${i + 1}`
          const pr = p && p.prompt ? String(p.prompt).trim() : ''
          return pr ? `[提示词 ${lab}] ${pr}` : ''
        })
        .filter(Boolean)
        .join('\n')
    )
  }
  if (imgs.length) parts.push(imgs.map((u, i) => `[详情图${i + 1}] ${u}`).join('\n'))
  return parts.join('\n').trim()
}
